import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Button,
  Divider,
  Icon,
  Label,
  List,
  Modal,
  Form,
  TextArea,
  Popup
} from 'semantic-ui-react';
import { CustomButton } from '../../../components/common';
import arrowLeft from '../../../assets/arrow-left.svg';
import readerIconA from '../../../assets/reader-icon-c.svg';
import applyIcon from '../../../assets/apply-icon.svg';
import emailIconA from '../../../assets/reader-icon-c.svg';

import {
  fetchOpportunity,
  selectSelectedOpportunity,
  selectOpportunitiesStatus,
  selectOpportunitiesError
} from '../../../slices/opportunityDataSlice';
import {
  fetchActorApplications,
  selectActorApplications
} from '../../../slices/applicationSlice';
import OpportunityStatusBadge from '../../../components/opportunities/OpportunityStatusBadge';
import { HeaderAndPage } from '../../../common/components';
import { format } from 'date-fns';
import { fetchUserSignedUrlPathV2 } from '../../../utils/fetchSignedUrlByFolderV2';
import microphoneStand from '../../../assets/microphone-stand.svg';
import { useMessageContext } from '../../../components/messaging/MessageProvider';
import { sendMessage } from '../../../slices/messageSlice';
import { MESSAGE_TYPES } from '../../../constants/messageConstants';
import { AuthStdFooter } from "../../../utils/auth-std-footer";
import VariationBadgeList from '../../../components/opportunities/VariationBadgeList';
import VariationStatusLegend from '../../../components/opportunities/VariationStatusLegend';
// Inline ⓥ trigger that opens the Character Context Hover Panel — same
// affordance used in OpportunityCard and OpportunitySpreadsheet. One
// viewport-docked positioning model shared across all three surfaces. See:
// docs/journal/PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §1.1
import CharacterContextTrigger from '../../../components/characterContext/CharacterContextTrigger';



/**
 * OpportunityDetails component displays the details of a specific opportunity
 */
const OpportunityDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get referrer from location state, default to /opportunities
  const referrer = location.state?.from || '/opportunities';
  const backButtonText = location.state?.backButtonText || 'Back';

  // Get opportunity from Redux store
  const opportunity = useSelector(selectSelectedOpportunity);
  const status = useSelector(selectOpportunitiesStatus);
  const error = useSelector(selectOpportunitiesError);

  // Get the current user and actor ID
  const currentUser = useSelector(state => state.user.profile?.data);

  // Conservative fix: Use userId as actorId for all users
  // This assumes all users should have an ActorUser node with uuid=userId
  // when acting as a Voice Actor (which is the intended architecture)
  const actorId = currentUser?.userId;

  // Get applications for the current actor
  const actorApplications = useSelector(selectActorApplications(actorId || ''));

  // State for book cover signed URL
  const [bookCoverSignedUrl, setBookCoverSignedUrl] = useState(null);
  const [bookCoverError, setBookCoverError] = useState(false);

  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Get messaging context
  const { canSendMessage } = useMessageContext();

  // Check if the current user is the creator of the opportunity
  const isCreator = opportunity?.creator?.userId === currentUser?.userId;

  // Check if the current actor has applied for this opportunity
  const hasApplied = actorApplications.some(app => app.opportunity?.uuid === id);
  const application = actorApplications.find(app => app.opportunity?.uuid === id);

  // Fetch opportunity and applications on component mount
  useEffect(() => {
    if (id) {
      dispatch(fetchOpportunity(id));

      if (actorId) {
        dispatch(fetchActorApplications(actorId));
      }
    }
  }, [dispatch, id, actorId]);

  // Handle book cover signed URL conversion
  useEffect(() => {
    const convertBookCoverToSignedUrl = async () => {
      console.log("[BOOK_PRFILE] bookCover profile", opportunity?.book?.profile)
      if (opportunity?.book?.profile?.bookCover && currentUser?.userId) {
        try {
          setBookCoverError(false);
          const signedUrl = await fetchUserSignedUrlPathV2(
            currentUser.userId,
            opportunity.book.profile.bookCover
          );
          console.log("[BOOK_PRFILE] bookCover userId", currentUser.userId, " signedUrl", signedUrl)

          if (signedUrl) {
            setBookCoverSignedUrl(signedUrl);
          } else {
            setBookCoverError(true);
          }
        } catch (error) {
          console.error('Error converting book cover to signed URL:', error);
          setBookCoverError(true);
        }
      } else {
        // Reset state if no book cover or user
        setBookCoverSignedUrl(null);
        setBookCoverError(false);
      }
    };

    convertBookCoverToSignedUrl();
  }, [opportunity?.book?.profile?.bookCover, currentUser?.userId]);

  // Handle apply button click - same as in OpportunityMarketplace
  const handleApply = () => {
    navigate(`/opportunities/${id}/apply`, {
      state: {
        from: location.pathname + location.search,
        backButtonText: 'Back to Opportunity Details'
      }
    });
  };

  // Handle view applications button click
  const handleViewApplications = () => {
    navigate(`/opportunities/${id}/applications`, {
      state: {
        from: location.pathname + location.search,
        backButtonText: 'Back to Opportunity Details'
      }
    });
  };

  // Handle view application button click
  const handleViewApplication = () => {
    if (application) {
      navigate(`/applications/${application.uuid}`, {
        state: {
          from: location.pathname + location.search,
          backButtonText: 'Back to Opportunity Details'
        }
      });
    }
  };

  // Handle contact creator button click
  const handleContactCreator = () => {
    if (!canSendMessage(MESSAGE_TYPES.OPPORTUNITY_INQUIRY)) {
      alert('You do not have permission to send messages.');
      return;
    }

    // Generate initial message content
    const initialMessage = `Hi ${opportunity.creator?.name || 'there'},

I'm interested in the voice acting opportunity for "${opportunity.title}". I'd like to learn more about the project and discuss how I might be a good fit for this role.

Some questions I have:
- What is the expected timeline for completion?
- Are there any specific voice characteristics you're looking for?
- Would you like to hear a sample of my work?

I look forward to hearing from you!

Best regards,
${currentUser?.name || 'Voice Actor'}`;

    setMessageContent(initialMessage);
    setShowMessageModal(true);
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!messageContent.trim()) {
      alert('Please enter a message.');
      return;
    }

    setIsSendingMessage(true);
    try {
      const messageData = {
        senderId: currentUser.userId,
        recipientId: opportunity.creator.userId,
        content: messageContent,
        messageType: MESSAGE_TYPES.OPPORTUNITY_INQUIRY,
        metadata: {
          opportunityId: opportunity.uuid,
          opportunityTitle: opportunity.title,
          timestamp: new Date().toISOString()
        }
      };

      await dispatch(sendMessage(messageData));

      setShowMessageModal(false);
      setMessageContent('');

      // Show success message
      alert('Message sent successfully!');

    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Handle close message modal
  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setMessageContent('');
  };

  // Format the deadline date if it exists
  const formattedDeadline = opportunity?.deadline
    ? format(new Date(opportunity.deadline), 'MMMM d, yyyy')
    : 'No deadline';

  // Format the creation date if it exists
  const formattedCreatedAt = opportunity?.createdAt
    ? format(new Date(opportunity.createdAt), 'MMMM d, yyyy')
    : 'Unknown date';



  const processErrors = (status, error, opportunity) => {
    let results = null;
    if (status === 'failed') {
      results = (
        <HeaderAndPage
          headerImage={microphoneStand}
          headerText="Opportunity Details"
          subHeaderText="Error loading opportunity information"
          doCenter={true}
        >
          <Message negative>
            <Message.Header>Error Loading Opportunity</Message.Header>
            <p>{error}</p>
            <CustomButton
              color="blue"
              onClick={() => navigate(-1)}
              icon={arrowLeft}
              size="tiny"
              style={{ marginTop: '10px' }}
            >
              {backButtonText}
            </CustomButton>
          </Message>
        </HeaderAndPage>
      );
    }
    if (status !== 'loading' && !opportunity) {
      results = (
        <HeaderAndPage
          headerImage={microphoneStand}
          headerText="Opportunity Details"
          subHeaderText="Opportunity not found"
          doCenter={true}
        >
          <Message warning>
            <Message.Header>Opportunity Not Found</Message.Header>
            <p>The opportunity you are looking for does not exist or has been removed.</p>
            <CustomButton
              color="blue"
              onClick={() => navigate(-1)}
              icon={arrowLeft}
              size="tiny"
              style={{ marginTop: '10px' }}
            >
              {backButtonText}
            </CustomButton>
          </Message>
        </HeaderAndPage>
      );
    }

    return results;
  }


  let displayPage = processErrors(status, error, opportunity);
  if (displayPage !== null) return displayPage;
  // Render opportunity details
  if (!opportunity) return <div></div>
  return (
    <HeaderAndPage
      headerImage={microphoneStand}
      headerText="Opportunity Details"
      subHeaderText={opportunity.title}
      doCenter={true}
    >
      <Segment style={{ marginBottom: "70px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <Header as="h2">{opportunity.title}</Header>
              <div style={{ marginBottom: '15px' }}>
                <OpportunityStatusBadge
                  status={opportunity.status}
                  priority={opportunity.priority}
                  deadline={opportunity.deadline}
                  size="tiny"
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} textAlign="right">
              {/* Show Apply/View Application buttons for everyone when opportunity is Open */}
              {opportunity.status === 'Open' && (
                <div>
                  {hasApplied ? (
                    <CustomButton
                      color="green"
                      onClick={handleViewApplication}
                      icon={readerIconA}
                      style={{ marginBottom: '10px', width: '100%' }}
                      size="tiny"
                    >
                      View Your Application
                    </CustomButton>

                  ) : (
                    <CustomButton
                      color="green"
                      onClick={handleApply}
                      icon={applyIcon}
                      style={{ marginBottom: '10px', width: '70%' }}
                      size="tiny"
                    >
                      Apply Now
                    </CustomButton>


                  )}
                  {!isCreator && (
                    <CustomButton
                      color="blue"
                      onClick={handleContactCreator}
                      style={{ width: '100%' }}
                      icon={emailIconA}
                      size="tiny"
                    >
                      Contact Creator
                    </CustomButton>
                  )}
                </div>
              )}
              {/* Show management button for creators */}
              {isCreator && (
                <Button
                  color="blue"
                  onClick={handleViewApplications}
                  style={{ marginTop: opportunity.status === 'Open' ? '10px' : '0', width: '100%' }}
                >
                  <Icon name="users" /> View Applications
                </Button>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Divider />

        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as="h3">Description</Header>
              <p style={{ whiteSpace: 'pre-line' }}>{opportunity.description}</p>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <Segment>
                <Header as="h4">
                  Book Information
                  {isCreator && (
                    <Popup
                      content="This is your book. You can apply to voice characters or manage applications."
                      position='top center'
                      trigger={
                        <Label size='mini' color='blue' style={{ marginLeft: '10px' }}>
                          <Icon name='user' /> Your Book
                        </Label>
                      }
                    />
                  )}
                </Header>
                {opportunity.book ? (
                  <Grid>
                    <Grid.Row>
                      {/* Book Cover Image */}


                      {/* Book Details */}
                      <Grid.Column width={(bookCoverSignedUrl && !bookCoverError) ? 12 : 16}>
                        <List>
                          <List.Item>
                            <List.Icon name="book" />
                            <List.Content>
                              <List.Header>{opportunity.book.name}</List.Header>
                              <List.Description>
                                by {opportunity.book.author?.name || 'Unknown author'}
                              </List.Description>
                              {opportunity.book.profile?.description && (
                                <List.Description style={{ marginTop: '5px', fontStyle: 'italic', fontSize: '0.9em' }}>
                                  {opportunity.book.profile.description.length > 100
                                    ? `${opportunity.book.profile.description.substring(0, 100)}...`
                                    : opportunity.book.profile.description
                                  }
                                </List.Description>
                              )}
                            </List.Content>
                          </List.Item>
                        </List>
                      </Grid.Column>
                      {(bookCoverSignedUrl && !bookCoverError) && (
                        <Grid.Column width={4}>
                          <Image
                            src={bookCoverSignedUrl}
                            size="tiny"
                            style={{ height: '60px', width: 'auto', objectFit: 'cover' }}
                            alt={`${opportunity.book.name} cover`}
                            onError={() => setBookCoverError(true)}
                          />
                        </Grid.Column>
                      )}
                    </Grid.Row>
                  </Grid>
                ) : (
                  <p>No book information available</p>
                )}
              </Segment>
            </Grid.Column>

            <Grid.Column width={8}>
              <Segment>
                {/* Section-header row: title on the left, Character Context
                 * Hover Panel trigger (actionButton variant) right-aligned.
                 * Promoting the trigger from a name-suffix glyph to a
                 * section-level button reads as "section-level action on
                 * this character" and matches the affordance pattern used
                 * in the marketplace spreadsheet Actions cell. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '1em',
                  }}
                >
                  <Header as="h4" style={{ margin: 0 }}>Character Information</Header>
                  {opportunity.character?.name && (
                    <CharacterContextTrigger
                      variant="actionButton"
                      bookUuid={opportunity.book?.uuid}
                      characterId={opportunity.character.uuid}
                      characterName={opportunity.character.name}
                      bookTitle={opportunity.book?.name}
                    />
                  )}
                </div>
                {opportunity.character ? (
                  <List>
                    <List.Item>
                      <List.Icon name="user" />
                      <List.Content>
                        <List.Header>
                          {opportunity.character.name}
                        </List.Header>
                        <List.Description>
                          {opportunity.character.description || 'No character description available'}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                  </List>
                ) : (

                  <p>No character information available</p>
                )}
              </Segment>
            </Grid.Column>

          </Grid.Row>

          {/* Character Variations (emotion badges from HAS_OPPORTUNITY_VARIATION) */}
          {opportunity.variations && opportunity.variations.length > 0 && (
            <Grid.Row>
              <Grid.Column width={8}>
                <Segment>
                  <Header as="h4">
                    <Icon name="users" />
                    Character Variations
                    <Label circular size="mini" style={{ marginLeft: '8px' }}>
                      {opportunity.variations.length}
                    </Label>
                    {/* Accessible status legend — explains the colored /
                        iconed variation badges below so color-blind users
                        (and anyone new to the UI) can decode each chip. */}
                    <span style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
                      <VariationStatusLegend compact />
                    </span>
                  </Header>

                  <p style={{ color: '#666', fontSize: '0.9em', marginBottom: '10px' }}>
                    This character requires the following emotional variations for recording:
                  </p>
                  <VariationBadgeList variations={opportunity.variations} columns={5} />
                </Segment>
              </Grid.Column>
              <Grid.Column width={8}>
                <Segment>
                  <Header as="h4">Requirements</Header>
                  {opportunity.requirements && opportunity.requirements.length > 0 ? (
                    <List bulleted>
                      {opportunity.requirements.map((requirement, index) => (
                        <List.Item key={index}>{requirement}</List.Item>
                      ))}
                    </List>
                  ) : (
                    <p>No specific requirements</p>
                  )}
                </Segment>
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column width={8}>
              <Segment>
                <Header as="h4">Compensation</Header>
                <p>
                  <Icon name="dollar" />
                  <strong>Amount:</strong> {opportunity.compensation || 'Not specified'}
                </p>
                <p>
                  <Icon name="info circle" />
                  <strong>Type:</strong> {opportunity.compensationType || 'Not specified'}
                </p>
              </Segment>
            </Grid.Column>
            <Grid.Column width={8}>
              <Segment>
                <Header as="h4">Additional Information</Header>
                <Grid columns={2} stackable>
                  <Grid.Column>
                    <p>
                      <Icon name="calendar" />
                      <strong>Deadline:</strong> {formattedDeadline}
                    </p>
                    <p>
                      <Icon name="calendar check" />
                      <strong>Posted:</strong> {formattedCreatedAt}
                    </p>
                  </Grid.Column>
                  <Grid.Column>
                    <p>
                      <Icon name="user" />
                      <strong>Posted by:</strong> {opportunity.creator?.email || 'Unknown'}
                    </p>
                    <p>
                      <Icon name="tag" />
                      <strong>Status:</strong> {opportunity.status}
                    </p>
                  </Grid.Column>
                </Grid>
              </Segment>
            </Grid.Column>

          </Grid.Row>
        </Grid>

        <Divider />

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <CustomButton
            color="grey"
            onClick={() => navigate(-1)}
            icon={arrowLeft}
            style={{ marginLeft: '10px' }}
            size="tiny"
          >
            {backButtonText}
          </CustomButton>
          {/* Apply/View Application buttons for everyone when opportunity is Open */}
          {opportunity.status === 'Open' && !hasApplied && (
            <CustomButton
              color="green"
              onClick={handleApply}
              icon={applyIcon}
              style={{ marginLeft: '10px' }}
              size="tiny"
            >
              Apply Now
            </CustomButton>
          )}

          {hasApplied && (
            <CustomButton
              color="green"
              onClick={handleViewApplication}
              icon={readerIconA}
              style={{ marginLeft: '10px' }}
              size="tiny"
            >
              View Your Application
            </CustomButton>
          )}

          {/* Contact Creator button only for non-creators */}
          {!isCreator && opportunity.status === 'Open' && (
            <CustomButton
              color="blue"
              onClick={handleContactCreator}
              style={{ marginLeft: '10px' }}
              icon={emailIconA}
              size='tiny'
            >
              Contact Creator
            </CustomButton>
          )}

          {/* View Applications button for creators */}
          {isCreator && (
            <CustomButton
              color="blue"
              onClick={handleViewApplications}
              icon={readerIconA}
              size="tiny"
              style={{ marginLeft: "10px" }}
            >
              View Applications
            </CustomButton>
          )}
        </div>
      </Segment>

      {/* Message Modal */}
      <Modal
        open={showMessageModal}
        onClose={handleCloseMessageModal}
        size="small"
      >
        <Modal.Header>
          <Icon name="mail" />
          Contact Opportunity Creator
        </Modal.Header>
        <Modal.Content>
          <Message info>
            <Message.Header>Sending message to: {opportunity.creator?.name || opportunity.creator?.email}</Message.Header>
            <p>Regarding opportunity: <strong>{opportunity.title}</strong></p>
          </Message>

          <Form>
            <Form.Field>
              <label>Your Message</label>
              <TextArea
                placeholder="Enter your message here..."
                value={messageContent}
                onChange={(e, { value }) => setMessageContent(value)}
                rows={10}
                style={{ minHeight: '200px' }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseMessageModal}>
            <Icon name="cancel" />
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleSendMessage}
            loading={isSendingMessage}
            disabled={!messageContent.trim()}
          >
            <Icon name="send" />
            Send Message
          </Button>
        </Modal.Actions>
      </Modal>
      {AuthStdFooter()}
    </HeaderAndPage>
  );
}

export default OpportunityDetails;
